import { PriceSummary } from '@qbe/ads-blocks-forms';
import { Box } from '@qbe/ads-core';
import React, { useMemo } from 'react';

import ButtonProgressIcon from '~/components-ds/ButtonProgressIcon';
import { CLASS_TYPE_TO_COVER } from '~/constants/domain-specific';
import { useAppContent, useAppDispatch, useQuote } from '~/hooks';
import { useBreakpoint } from '~/hooks-ds/useBreakpoint';
import { qalTrackEngagementAction } from '~/thunks/analytics';
import { analyticsConstant } from '~/types/analytics';
import { getPremiumClasses } from '~/utils';
import { currencyFormatter } from '~/utils/currencyFormatter';

interface PremiumPanelProps {
  readOnly?: boolean;
  onBuyNow?: (e: React.MouseEvent<HTMLElement>) => void;
  isLoading?: boolean;
}

const PremiumPanel: React.FC<PremiumPanelProps> = ({ onBuyNow, readOnly = false, isLoading }) => {
  const { getTextContent } = useAppContent({ basePath: 'dsme.review' });
  const dispatch = useAppDispatch();
  const { premiums } = useQuote();
  const premium = premiums?.policy?.policyPremium?.transactionPremium?.totalPremium;
  const levies = premiums?.policy?.policyPremium?.transactionPremium?.FSL;
  const stampduty = premiums?.policy?.policyPremium?.transactionPremium?.duty;
  const totalGst = premiums?.policy?.policyPremium?.transactionPremium?.GST;
  const additionalFields = useMemo(
    () => [
      {
        name: getTextContent('fields.premiumPanel.levies'),
        amount: currencyFormatter(levies),
      },
      {
        name: getTextContent('fields.premiumPanel.gst'),
        amount: currencyFormatter(totalGst),
      },
      {
        name: getTextContent('fields.premiumPanel.stampDuty'),
        amount: currencyFormatter(stampduty),
      },
    ],
    [levies, totalGst, stampduty, getTextContent]
  );

  const fields = premiums?.policy?.risks ? getPremiumClasses(premiums?.policy.risks) : null;

  const { isDesktop } = useBreakpoint();

  const onChangeHandler = (event: React.ChangeEvent<unknown>, expanded: boolean) => {
    dispatch(
      qalTrackEngagementAction({
        eventAction: `premium panel - Accordion: ${expanded ? 'expanded' : 'contracted'}`,
        eventLabel: analyticsConstant.NOT_SET,
        data: {},
      })
    );
  };

  const lists = useMemo(() => {
    const val = [
      {
        title: getTextContent('fields.premiumPanel.subtitle'),
        items: additionalFields.map((f) => ({
          name: f.name,
          value: f.amount,
        })),
      },
    ];
    if (fields) {
      // unshift - tack to the start of the array
      val.unshift({
        title: '',
        items: fields?.map((f) => ({
          name: f.classType && CLASS_TYPE_TO_COVER[f.classType],
          value: currencyFormatter(f.amount),
        })),
      });
    }
    return val;
  }, [fields, additionalFields, getTextContent]);

  return (
    <Box // TODO - no space between long label and amount, whole element can be removed - https://qbe-appservices.atlassian.net/browse/AD1-588
      sx={{
        '& li > p:first-of-type': {
          paddingRight: 1,
        },
      }}
    >
      <PriceSummary
        defaultExpanded={readOnly ? false : isDesktop}
        onChange={onChangeHandler}
        headerProps={{
          title: getTextContent('fields.premiumPanel.title'),
          value: currencyFormatter(premium),
        }}
        lists={lists}
        footerProps={
          // TODO should render only for condition - !readOnly && - https://qbe-appservices.atlassian.net/browse/AD1-540
          {
            description: getTextContent('fields.premiumPanel.description'),
            buttonProps: {
              disabled: isLoading,
              label: getTextContent('buttons.buyNow'),
              onClick: onBuyNow,
              endIcon: isLoading && <ButtonProgressIcon />,
              'data-track': 'buy now',
            },
          }
        }
      />
    </Box>
  );
};

export default PremiumPanel;
